import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Printer, ArrowLeft, LifeBuoy, Save, Download, Send } from "lucide-react";
import axios from "axios";
import { FIRM } from "@/lib/firm";
import { dash, money, fmtDate, borrowerSigOf } from "@/pages/CollateralMotion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useQuery = () => new URLSearchParams(useLocation().search);
const todayISO = () => new Date().toISOString().slice(0, 10);

const CursiveSig = ({ name }) => name
  ? <span className="lg-sig-cursive" style={{ fontSize: "22pt", lineHeight: 1 }}>{name.startsWith("/s/") ? name : `/s/ ${name}`}</span>
  : <span>______________________________</span>;

export const LM_CSS = `
        .lm-sheet{background:#fff;color:#141410;max-width:8.5in;margin:0 auto 28px;padding:0.8in 0.75in;box-shadow:0 10px 40px rgba(0,0,0,.4);font-family:'Times New Roman',Georgia,serif;font-size:11pt;line-height:1.45;}
        .lm-head{text-align:center;border-bottom:2px solid #123a2e;padding-bottom:10px;margin-bottom:12px;}
        .lm-eyebrow{font-size:9pt;letter-spacing:.14em;color:#123a2e;font-weight:700;text-transform:uppercase;}
        .lm-title{font-size:15pt;font-weight:700;margin:5px 0 3px;text-transform:uppercase;}
        .lm-sub{font-size:9pt;color:#444;font-style:italic;}
        .lm-formhdr{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:1.5px solid #123a2e;padding-bottom:5px;margin:2px 0 8px;}
        .lm-fno{font-size:9pt;font-weight:700;color:#123a2e;letter-spacing:.05em;}
        .lm-fname{font-size:13pt;font-weight:700;text-transform:uppercase;}
        .lm-frev{font-size:8pt;color:#666;}
        .lm-sheet p{text-align:justify;margin:6px 0;}
        .lm-f{display:flex;gap:8px;padding:2.5px 0;border-bottom:1px dotted #ddd;}
        .lm-fl{flex:0 0 40%;font-weight:600;color:#333;}
        .lm-fv{flex:1;}
        .lm-blank{display:inline-block;min-width:200px;border-bottom:1px solid #333;}
        .lm-h{font-size:11pt;font-weight:700;color:#123a2e;margin:12px 0 5px;}
        .lm-check{margin:2px 0;}
        .lm-cols{column-count:2;column-gap:26px;margin:4px 0;}
        .lm-table{width:100%;border-collapse:collapse;margin:8px 0;font-size:9.5pt;}
        .lm-table th,.lm-table td{border:1px solid #b6ccc2;padding:4px 6px;text-align:left;vertical-align:top;}
        .lm-table th{background:#eaf3ef;color:#123a2e;font-weight:700;}
        .lm-sig{margin:10px 0;} .lm-sig p{margin:4px 0;}
        .lm-sigrow{display:flex;gap:24px;margin-top:8px;} .lm-sigrow>div{flex:1;}
        .lm-form{page-break-before:always;}
        .lm-note{font-size:8.5pt;color:#5C5C5C;}
        @media print{
          body{background:#fff!important;}
          .no-print{display:none!important;}
          .cm-print-area{padding:0!important;max-width:none!important;}
          .lm-sheet{box-shadow:none!important;margin:0!important;max-width:none!important;padding:0.5in!important;}
          .lm-h,.lm-fname{break-after:avoid;}
          @page{size:letter;margin:0.5in;}
        }
`;

const B = () => <span className="lm-blank" />;
const F = ({ label, children }) => (
  <div className="lm-f"><span className="lm-fl">{label}</span><span className="lm-fv">{children || <B />}</span></div>
);
const FormHeader = ({ no, name, rev }) => (
  <div className="lm-formhdr">
    <div><div className="lm-fno">{no}</div><div className="lm-fname">{name}</div></div>
    <div className="lm-frev">{rev}</div>
  </div>
);
const Check = ({ on, children }) => <div className="lm-check">{on ? "☑" : "☐"} {children}</div>;
const val = (shared, key, auto) => (shared[key] !== undefined && shared[key] !== "" ? shared[key] : (auto || ""));
const mask = (ssn) => { const d = String(ssn || "").replace(/[^0-9]/g, ""); return d.length >= 4 ? `***-**-${d.slice(-4)}` : ""; };

export const lossMitDefaults = () => ({
  date: todayISO(), loanNo: "", servicer: "", occupancy: "Primary Residence",
  intention: "Keep the property", hardshipType: "Reduction in income", hardshipDuration: "Long-term or permanent",
  hardshipDate: "", monthlyIncome: "", monthlyExpenses: "", relief: "Loan modification",
  proposalNote: "", ivesParticipant: "", borrower: "", coBorrower: "", address: "", phone: "", email: "",
});

const HARDSHIP_TYPES = ["Unemployment", "Reduction in income", "Increase in housing-related expenses", "Disaster (natural or man-made)", "Long-term or permanent disability, or serious illness", "Divorce or legal separation", "Separation of borrowers unrelated by marriage, civil union, or similar", "Death of a borrower or death of the primary/secondary wage earner", "Distant employment transfer / relocation", "Business failure", "Other"];
const RELIEF_OPTIONS = ["Reinstatement", "Repayment plan", "Forbearance", "Loan modification", "Interest-rate reduction", "Principal reduction / deferral", "Short sale", "Deed-in-lieu of foreclosure"];

// Curated loss-mitigation fax numbers for major U.S. servicers (verify against the borrower's
// most recent statement/notice before sending — servicers reorganize these lines periodically).
const SERVICER_FAX = [
  { n: "Mr. Cooper (Nationstar)", f: "+12144881993" },
  { n: "PennyMac", f: "+18009471421" },
  { n: "Rocket Mortgage", f: "+18773823138" },
  { n: "Chase (JPMorgan)", f: "+18888742664" },
  { n: "Wells Fargo", f: "+18668379108" },
  { n: "Wells Fargo (alt)", f: "+18665908910" },
  { n: "Newrez", f: "+18664671187" },
  { n: "Shellpoint (Newrez)", f: "+18664671187" },
  { n: "Select Portfolio Servicing (SPS)", f: "+18012694459" },
  { n: "SPS (alt)", f: "+18012933936" },
  { n: "Carrington Mortgage", f: "+18004865134" },
  { n: "Carrington (loss draft)", f: "+18663280330" },
  { n: "Freedom Mortgage", f: "+18665050949" },
];

export function LossMitigationDoc({ app, shared, last }) {
  const auto = {
    borrower: app.borrower_full_name || app.borrower_name || "",
    coBorrower: app.co_borrower_name || "",
    address: app.borrower_address || "",
    phone: app.borrower_phone || "",
    email: app.borrower_email || "",
    servicer: app.current_lender || "",
    loanNo: app.loan_account_no || app.account_no || app.loan_number || "",
  };
  const g = (k) => val(shared, k, auto[k]);
  const property = app.property_or_collateral || app.property || app.borrower_address || "";
  const balance = app.current_balance;
  const bSig = borrowerSigOf(app);

  return (
    <div className="lm-sheet" data-testid={`lm-doc-${app.id}`} style={{ pageBreakAfter: last ? "auto" : "always" }}>
      {/* Cover: Loss Mitigation / Workout Proposal */}
      <div className="lm-head">
        <div className="lm-eyebrow">Loss Mitigation · In Lieu of Foreclosure · Reg X 12 CFR 1024.41</div>
        <div className="lm-title">Loss Mitigation &amp; Workout Proposal</div>
        <div className="lm-sub">Uniform Borrower Assistance (Form 710) · Dodd-Frank Certification · Hardship Affidavit · IRS 4506-C · CFPB Reg X Acknowledgment</div>
      </div>
      <F label="Date">{g("date") ? fmtDate(g("date")) : ""}</F>
      <F label="To — Lender / Servicer">{g("servicer")}</F>
      <F label="Borrower(s)">{[g("borrower"), g("coBorrower")].filter(Boolean).join(" &amp; ")}</F>
      <F label="Loan / Account No.">{g("loanNo")}</F>
      <F label="Property">{property}</F>
      <F label="Unpaid Balance">{balance != null ? money(balance) : ""}</F>
      <p>The Borrower submits this complete loss-mitigation application and requests that the Lender/Servicer review the enclosed Uniform Borrower Assistance Form (Form 710) and supporting documents, and evaluate the Borrower for all available loss-mitigation options in accordance with 12 CFR 1024.41 (Regulation X), applicable investor guidelines, and program rules.</p>
      <p style={{ fontWeight: 700 }}>Relief requested (check all that apply):</p>
      <div className="lm-cols">{RELIEF_OPTIONS.map((o, i) => <Check key={i} on={(g("relief") || "").toLowerCase().includes(o.split(" ")[0].toLowerCase())}>{o}</Check>)}</div>
      <F label="Primary Relief Requested">{g("relief")}</F>
      <p>{g("proposalNote") || "The Borrower is prepared to document income and hardship, execute a trial period plan or workout agreement where offered, and cooperate with all reasonable requests to complete the review. This submission is made without prejudice and all rights are reserved."}</p>
      <div className="lm-sig">
        <p>Borrower Signature: <CursiveSig name={bSig} /> — {g("borrower") || <B />}</p>
        <p>Date: {g("date") ? fmtDate(g("date")) : <B />}</p>
      </div>

      {/* Form 710 */}
      <div className="lm-form">
        <FormHeader no="FANNIE MAE / FREDDIE MAC FORM 710" name="Uniform Borrower Assistance Form" rev="Mortgage Assistance Application" />
        <div className="lm-h">Loan &amp; Contact Information</div>
        <F label="Servicer / Lender">{g("servicer")}</F>
        <F label="Loan Number">{g("loanNo")}</F>
        <F label="Borrower Name">{g("borrower")}</F>
        <F label="Co-Borrower Name">{g("coBorrower")}</F>
        <F label="Social Security No. (Borrower)">{mask(app.borrower_ssn)}</F>
        <F label="Social Security No. (Co-Borrower)">{mask(app.co_borrower_ssn)}</F>
        <F label="Property Address">{property}</F>
        <F label="Mailing Address (if different)">{g("address")}</F>
        <F label="Primary Phone">{g("phone")}</F>
        <F label="Email">{g("email")}</F>
        <div className="lm-h">Property &amp; Intention</div>
        <F label="Property is my">{g("occupancy")}</F>
        <F label="The property is">{"Owner-occupied · a rental · vacant (as applicable)"}</F>
        <F label="I want to">{g("intention")}</F>
        <div className="lm-h">Hardship</div>
        <p style={{ fontWeight: 700 }}>The hardship causing mortgage payment challenges began on approximately {g("hardshipDate") ? fmtDate(g("hardshipDate")) : "____________"} and is believed to be:</p>
        <F label="Hardship Duration">{g("hardshipDuration")}</F>
        <p style={{ fontWeight: 700 }}>Type of hardship (check all that apply):</p>
        <div className="lm-cols">{HARDSHIP_TYPES.map((h, i) => <Check key={i} on={(g("hardshipType") || "").toLowerCase().includes(h.split(" ")[0].toLowerCase())}>{h}</Check>)}</div>
        <div className="lm-h">Monthly Household Income</div>
        <table className="lm-table"><thead><tr><th>Income Source</th><th>Monthly Amount</th></tr></thead><tbody>
          {["Gross wages / salary", "Overtime / bonuses / commissions", "Self-employment income", "Unemployment income", "Social Security / disability / pension", "Rental income", "Other income"].map((r, i) => (
            <tr key={i}><td>{r}</td><td>&nbsp;</td></tr>
          ))}
          <tr><td style={{ fontWeight: 700 }}>Total Monthly Gross Income</td><td>{g("monthlyIncome") ? money(g("monthlyIncome")) : "\u00a0"}</td></tr>
        </tbody></table>
        <div className="lm-h">Monthly Household Expenses / Debt</div>
        <table className="lm-table"><thead><tr><th>Expense / Debt</th><th>Monthly Amount</th></tr></thead><tbody>
          {["First mortgage payment (PITI)", "Second mortgage / HELOC", "Property taxes / insurance (if not escrowed)", "HOA / condo fees", "Auto loans", "Credit cards / installment loans", "Utilities / living expenses", "Other obligations"].map((r, i) => (
            <tr key={i}><td>{r}</td><td>&nbsp;</td></tr>
          ))}
          <tr><td style={{ fontWeight: 700 }}>Total Monthly Expenses</td><td>{g("monthlyExpenses") ? money(g("monthlyExpenses")) : "\u00a0"}</td></tr>
        </tbody></table>
        <div className="lm-h">Assets</div>
        <table className="lm-table"><thead><tr><th>Asset</th><th>Estimated Value</th></tr></thead><tbody>
          {["Checking / savings accounts", "Investments / stocks / bonds", "Other real estate (est. value)", "Other assets"].map((r, i) => <tr key={i}><td>{r}</td><td>&nbsp;</td></tr>)}
        </tbody></table>
        <p className="lm-note">Required income documentation (pay stubs, tax returns, profit-and-loss, benefit letters, bank statements) should be attached as applicable.</p>
        <div className="lm-h">Borrower / Co-Borrower Acknowledgment and Agreement</div>
        <p className="lm-note">I certify that all of the information in this document is truthful and the hardship identified contributed to my need for mortgage relief. I understand the Servicer may investigate the accuracy of my statements, may require me to provide supporting documentation, and that knowingly submitting false information may violate federal law.</p>
        <div className="lm-sigrow">
          <div><p>Borrower Signature: <CursiveSig name={bSig} /></p><p>{g("borrower") || <B />}</p><p>Date: {g("date") ? fmtDate(g("date")) : <B />}</p></div>
          <div><p>Co-Borrower Signature: ______________________</p><p>{g("coBorrower") || <B />}</p><p>Date: ____________</p></div>
        </div>
      </div>

      {/* Dodd-Frank Certification */}
      <div className="lm-form">
        <FormHeader no="COMPANION TO FORM 710" name="Dodd-Frank Certification" rev="Required with the Uniform Borrower Assistance Form" />
        <p>Under the Dodd-Frank Wall Street Reform and Consumer Protection Act, I/we certify that I/we have not been convicted within the last 10 years of any one of the following in connection with a mortgage or real estate transaction:</p>
        <ul style={{ paddingLeft: 22 }}>
          <li>felony larceny, theft, fraud, or forgery,</li>
          <li>money laundering, or</li>
          <li>tax evasion.</li>
        </ul>
        <p>I/we understand that the Servicer, the owner or guarantor of my mortgage loan, or their respective agents may investigate the accuracy of my statements by performing routine background checks, including automated searches of federal, state, and county databases, to confirm that I/we have not been convicted of such crimes. I/we also understand that knowingly submitting false information may violate federal law.</p>
        <div className="lm-sigrow">
          <div><p>Borrower Signature: <CursiveSig name={bSig} /></p><p>{g("borrower") || <B />}</p><p>Date: {g("date") ? fmtDate(g("date")) : <B />}</p></div>
          <div><p>Co-Borrower Signature: ______________________</p><p>{g("coBorrower") || <B />}</p><p>Date: ____________</p></div>
        </div>
      </div>

      {/* Hardship Affidavit */}
      <div className="lm-form">
        <FormHeader no="SWORN STATEMENT" name="Hardship Affidavit" rev="In support of the loss-mitigation application" />
        <F label="Borrower(s)">{[g("borrower"), g("coBorrower")].filter(Boolean).join(" & ")}</F>
        <F label="Loan / Account No.">{g("loanNo")}</F>
        <F label="Property">{property}</F>
        <p>Being duly sworn, I/we depose and state that I/we are experiencing a financial hardship that began on or about {g("hardshipDate") ? fmtDate(g("hardshipDate")) : "____________"}, the nature of which is <strong>{dash(g("hardshipType"))}</strong>, and which is expected to be <strong>{dash(g("hardshipDuration"))}</strong>. This hardship has directly affected my/our ability to make the scheduled mortgage payments on the above loan.</p>
        <p style={{ minHeight: 60 }}>Explanation of hardship: ______________________________________________________________________________________________________________________________________________________________________________</p>
        <p>I/we request that the Servicer consider this affidavit in evaluating me/us for all available loss-mitigation options. The statements herein are true and correct to the best of my/our knowledge.</p>
        <div className="lm-sig">
          <p>Borrower: <CursiveSig name={bSig} /> — {g("borrower") || <B />}</p>
          <p>Date: {g("date") ? fmtDate(g("date")) : <B />}</p>
          <p>Subscribed and sworn before me this ______ day of __________, 20____ — Notary Public, State of {dash(FIRM.notaryState)}. [Notary Seal]</p>
        </div>
      </div>

      {/* IRS Form 4506-C */}
      <div className="lm-form">
        <FormHeader no="IRS FORM 4506-C" name="IVES Request for Transcript of Tax Return" rev="Department of the Treasury — Internal Revenue Service" />
        <F label="1a. Name shown on tax return (Borrower)">{g("borrower")}</F>
        <F label="1b. First SSN / ITIN / EIN">{mask(app.borrower_ssn)}</F>
        <F label="2a. Second name shown (Co-Borrower / spouse)">{g("coBorrower")}</F>
        <F label="2b. Second SSN / ITIN">{mask(app.co_borrower_ssn)}</F>
        <F label="3. Current name &amp; address">{[g("borrower"), g("address")].filter(Boolean).join(" · ")}</F>
        <F label="4. Previous address shown on last return (if different)">{""}</F>
        <F label="5a. IVES participant name, address &amp; SOR mailbox ID">{g("ivesParticipant")}</F>
        <F label="6. Transcript requested (form number)">{"1040 / 1065 / 1120 (as applicable)"}</F>
        <div className="lm-cols">
          <Check on>6a. Return transcript</Check>
          <Check>6b. Account transcript</Check>
          <Check>6c. Record of account</Check>
          <Check>8. Form W-2, 1099, 1098 series</Check>
        </div>
        <F label="9. Year or period requested (MM/DD/YYYY)">{""}</F>
        <p className="lm-note">Signatory attests to authority to sign and requests transcripts be sent to the IVES participant listed in line 5a. Caution: this tax information authorization must be signed and dated within 120 days of IRS receipt.</p>
        <div className="lm-sigrow">
          <div><p>Signature of taxpayer: <CursiveSig name={bSig} /></p><p>Date: {g("date") ? fmtDate(g("date")) : <B />}</p><p>Phone: {g("phone") || <B />}</p></div>
          <div><p>Spouse's signature: ______________________</p><p>Date: ____________</p></div>
        </div>
      </div>

      {/* CFPB Reg X Acknowledgment */}
      <div className="lm-form">
        <FormHeader no="CFPB REGULATION X" name="Loss Mitigation Acknowledgment (12 CFR 1024.41)" rev="RESPA servicing — borrower protections" />
        <p>This acknowledgment accompanies the Borrower's loss-mitigation application and confirms the parties' understanding of the following servicer obligations under 12 CFR 1024.41:</p>
        <ul style={{ paddingLeft: 22 }}>
          <li><strong>Acknowledgment of receipt.</strong> Within 5 days of receiving this application, the Servicer will acknowledge receipt in writing and state whether the application is complete or what additional documents are needed.</li>
          <li><strong>Evaluation.</strong> If a complete application is received more than 37 days before a foreclosure sale, the Servicer will evaluate the Borrower for all available loss-mitigation options and provide a written determination within 30 days.</li>
          <li><strong>Appeal.</strong> The Borrower may appeal a denial of a loan modification option within 14 days of the Servicer's determination where required by the rule.</li>
          <li><strong>Dual-tracking protections.</strong> The Servicer will not make the first notice or filing for foreclosure, or conduct a foreclosure sale, while a complete application is pending, consistent with the rule's protections.</li>
        </ul>
        <p className="lm-note">This acknowledgment does not waive any rights and all rights are reserved. It does not itself grant any loss-mitigation option; any relief is subject to the Servicer's evaluation, investor guidelines, and program rules.</p>
        <div className="lm-sigrow">
          <div><p>Borrower: <CursiveSig name={bSig} /></p><p>{g("borrower") || <B />}</p><p>Date: {g("date") ? fmtDate(g("date")) : <B />}</p></div>
          <div><p>Servicer / Lender Representative: ______________________</p><p>{g("servicer") || <B />}</p><p>Date: ____________</p></div>
        </div>
      </div>
    </div>
  );
}

const Ctl = ({ label, value, onChange, type = "text", options }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">{label}</label>
    {options
      ? <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#123a2e]">
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      : <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#123a2e]" />}
  </div>
);

export default function LossMitigationPackage() {
  const { appId } = useParams();
  const q = useQuery();
  const idsParam = q.get("ids");
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [shared, setShared] = useState(lossMitDefaults);
  const set = (k) => (v) => setShared((p) => ({ ...p, [k]: v }));

  const token = localStorage.getItem("th_admin_token");
  const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  useEffect(() => {
    const ids = idsParam ? idsParam.split(",").map((x) => x.trim()).filter(Boolean) : (appId ? [appId] : []);
    (async () => {
      const results = [];
      for (const id of ids) {
        try { const r = await axios.get(`${API}/borrower/applications/${id}`, cfg); if (r.data.application) results.push(r.data.application); } catch { /* skip */ }
      }
      if (results[0]) {
        const savedSettings = (results.find((a) => a.loss_mitigation_settings) || {}).loss_mitigation_settings || null;
        if (savedSettings) setShared((p) => ({ ...p, ...savedSettings }));
      }
      setApps(results);
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [appId, idsParam]);

  const saveDocData = async () => {
    if (!apps.length) return;
    setSaving(true); setSavedMsg("");
    try {
      await Promise.all(apps.map((a) => axios.post(`${API}/admin/borrower-applications/${a.id}/motion-data`,
        { loss_mitigation_settings: shared }, cfg)));
      setSavedMsg("Saved to file ✓"); setTimeout(() => setSavedMsg(""), 4000);
    } catch { setSavedMsg("Save failed"); setTimeout(() => setSavedMsg(""), 4000); }
    finally { setSaving(false); }
  };

  const [dlBusy, setDlBusy] = useState(false);
  const downloadPdf = async () => {
    if (!apps.length) return;
    setDlBusy(true);
    try {
      const r = await axios.post(`${API}/admin/loss-mitigation/bulk-pdf`, { app_ids: apps.map((a) => a.id) }, { ...cfg, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = apps.length > 1 ? `loss_mitigation_packages_${apps.length}.pdf` : "loss_mitigation_package.pdf";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || "Could not build the merged PDF."); }
    finally { setDlBusy(false); }
  };

  const [faxOpen, setFaxOpen] = useState(false);
  const [faxTo, setFaxTo] = useState("");
  const [faxBusy, setFaxBusy] = useState(false);
  const [faxMsg, setFaxMsg] = useState(null);
  const sendFax = async () => {
    if (!apps.length || !faxTo.trim()) return;
    setFaxBusy(true); setFaxMsg(null);
    try {
      const r = await axios.post(`${API}/admin/loss-mitigation/efax`, { app_ids: apps.map((a) => a.id), to: faxTo.trim(), label: `Loss Mitigation Package · ${apps.length} borrower(s)` }, cfg);
      const fid = r.data?.fax?.fax_id || r.data?.fax?.id || "";
      const st = r.data?.fax?.status || "queued";
      setFaxMsg({ ok: true, text: `✓ Fax submitted to ${faxTo.trim()} — ${apps.length} package(s). Telnyx ID: ${fid || "n/a"} · Status: ${st}` });
    } catch (e) { setFaxMsg({ ok: false, text: e.response?.data?.detail || "Fax failed to send." }); }
    finally { setFaxBusy(false); }
  };

  const servicerName = apps[0]?.current_lender || "";

  if (loading) return <div style={{ padding: 40, color: "#ECECE4", fontFamily: "serif" }}>Loading…</div>;

  return (
    <div style={{ background: "#0B0B07", minHeight: "100vh" }}>
      <div className="no-print" style={{ background: "#0C1613", borderBottom: "1px solid #123a2e", padding: "12px 18px" }}>
        <div style={{ maxWidth: "8.5in", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <a href="/admin" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#7FD3B4", fontSize: 12 }}><ArrowLeft size={14} /> Back</a>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#7FD3B4", fontWeight: 700, letterSpacing: ".06em", fontSize: 12 }}>
            <LifeBuoy size={16} /> LOSS MITIGATION PACKAGE{apps.length > 1 ? ` · ${apps.length} BORROWERS` : ""}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={saveDocData} disabled={saving || !apps.length} data-testid="lm-save" style={btn("#7FD3B4", "#2E6F5E")}><Save size={13} /> {saving ? "Saving…" : "Save to File"}</button>
            <button onClick={downloadPdf} disabled={dlBusy || !apps.length} data-testid="lm-download" style={btn("#7FD3B4", "#2E6F5E")}><Download size={13} /> {dlBusy ? "Building…" : "Download PDF"}</button>
            <button onClick={() => setFaxOpen((x) => !x)} disabled={!apps.length} data-testid="lm-efax-toggle" style={btn("#7FD3B4", "#2E6F5E")}><Send size={13} /> eFax</button>
            <button onClick={() => window.print()} data-testid="lm-print" style={{ ...btn("#0B0B07", "#B8860B"), background: "#E4C97A", color: "#0B0B07" }}><Printer size={13} /> Print / Save as PDF</button>
          </div>
        </div>
        {savedMsg && <div className="text-[12px] text-[#5FE0B0] mt-2" data-testid="lm-saved-msg" style={{ maxWidth: "8.5in", margin: "6px auto 0" }}>{savedMsg}</div>}

        {faxOpen && (
          <div style={{ maxWidth: "8.5in", margin: "10px auto 0" }} className="rounded-md border border-[#2E6F5E]/40 bg-[#0F1410] p-3" data-testid="lm-efax-panel">
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#7FD3B4] mb-2 flex items-center gap-2"><Send size={12} /> eFax to lender/servicer{apps.length > 1 ? ` (all ${apps.length} borrowers merged)` : ""}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ minWidth: 240 }}>
                <label className="block text-[10px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Saved servicer destination</label>
                <select onChange={(e) => e.target.value && setFaxTo(e.target.value)} data-testid="lm-efax-dir" className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#2E6F5E]">
                  <option value="">Choose a servicer…</option>
                  {SERVICER_FAX.map((d) => <option key={d.n} value={d.f}>{d.n} — {d.f}</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <label className="block text-[10px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Lender / servicer fax number{servicerName ? ` (for ${servicerName})` : ""}</label>
                <input value={faxTo} onChange={(e) => setFaxTo(e.target.value)} data-testid="lm-efax-to" placeholder="+1 555 123 4567"
                  className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#2E6F5E]" />
              </div>
              <button onClick={sendFax} disabled={faxBusy || !faxTo.trim()} data-testid="lm-efax-send" style={{ ...btn("#0B0B07", "#2E6F5E"), background: "#5FBFA0", color: "#0B0B07" }}><Send size={13} /> {faxBusy ? "Sending…" : "Send eFax"}</button>
            </div>
            {faxMsg && <div data-testid="lm-efax-msg" className={`text-[12px] font-bold mt-2 ${faxMsg.ok ? "text-[#3DBB8E]" : "text-[#FF8585]"}`}>{faxMsg.text}</div>}
            <div className="text-[10px] text-[#8A8A8A] mt-2">Saved numbers are the published loss-mitigation lines for major national servicers — <span className="text-[#E4C97A]">always confirm against the borrower's latest statement/notice before sending</span>, as servicers rotate these lines. Include the loan number on every page.</div>
          </div>
        )}

        <div style={{ maxWidth: "8.5in", margin: "12px auto 0" }} className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Ctl label="Date" value={shared.date} onChange={set("date")} type="date" />
          <Ctl label="Lender / Servicer (override)" value={shared.servicer} onChange={set("servicer")} />
          <Ctl label="Loan / Account No. (override)" value={shared.loanNo} onChange={set("loanNo")} />
          <Ctl label="Occupancy" value={shared.occupancy} onChange={set("occupancy")} options={["Primary Residence", "Second Home", "Investment Property"]} />
          <Ctl label="Intention" value={shared.intention} onChange={set("intention")} options={["Keep the property", "Sell the property", "Vacate / deed-in-lieu", "Undecided"]} />
          <Ctl label="Hardship Type" value={shared.hardshipType} onChange={set("hardshipType")} options={HARDSHIP_TYPES} />
          <Ctl label="Hardship Duration" value={shared.hardshipDuration} onChange={set("hardshipDuration")} options={["Short-term (under 6 months)", "Long-term or permanent"]} />
          <Ctl label="Hardship Begin Date" value={shared.hardshipDate} onChange={set("hardshipDate")} type="date" />
          <Ctl label="Primary Relief Requested" value={shared.relief} onChange={set("relief")} options={RELIEF_OPTIONS.map((o) => o)} />
          <Ctl label="Total Monthly Income ($)" value={shared.monthlyIncome} onChange={set("monthlyIncome")} type="number" />
          <Ctl label="Total Monthly Expenses ($)" value={shared.monthlyExpenses} onChange={set("monthlyExpenses")} type="number" />
          <Ctl label="IVES Participant (4506-C line 5a)" value={shared.ivesParticipant} onChange={set("ivesParticipant")} />
          <Ctl label="Borrower (override)" value={shared.borrower} onChange={set("borrower")} />
          <Ctl label="Co-Borrower (override)" value={shared.coBorrower} onChange={set("coBorrower")} />
          <Ctl label="Phone (override)" value={shared.phone} onChange={set("phone")} />
          <Ctl label="Email (override)" value={shared.email} onChange={set("email")} />
        </div>
        <div className="text-[10px] text-[#8A8A8A] mt-2" style={{ maxWidth: "8.5in", margin: "8px auto 0" }}>Borrower, co-borrower, servicer, loan number, property and balance auto-populate from each borrower's file (SSNs shown masked); anything blank prints as a fillable line. Overrides above apply to all selected borrowers. The Borrower e-signs each form.</div>
      </div>

      <div className="cm-print-area" style={{ padding: "24px 0" }}>
        {!apps.length
          ? <div style={{ color: "#9A9A90", textAlign: "center", padding: 40 }}>No application loaded. Open this from a borrower file, or pass ?ids=… for bulk.</div>
          : apps.map((a, i) => <LossMitigationDoc key={a.id} app={a} shared={shared} last={i === apps.length - 1} />)}
      </div>
      <style>{LM_CSS}</style>
    </div>
  );
}

const btn = (color, border) => ({ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", color, border: `1px solid ${border}`, padding: "9px 14px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", cursor: "pointer" });
